// 2026-05-26 - FormuLearning - 프로젝트 상태 관리 (Zustand)
// 2026-09-21 수정 - 이슈 #77(G6): 폴더 상태·액션 · 이름/Description 수정 · 프로젝트 이동 ·
//   목록 조용한 재조회(내부데이터 최종 수정일 정렬 반영)
// 파일 위치: frontend/src/stores/projectStore.ts

import { create } from 'zustand'
import type { ProjectListItem, ProjectDetail, ProjectGating, ProjectFolder } from '../types/project'
import * as projectApi from '../api/projects'
import { useSummaryStore } from './summaryStore'
import { useMoleculeStore } from './moleculeStore'

interface ProjectState {
  projects: ProjectListItem[]
  folders: ProjectFolder[]
  currentProject: ProjectDetail | null
  gating: ProjectGating | null
  loading: boolean
  fetchProjects: () => Promise<void>
  createProject: (name: string, memo?: string, folderId?: string | null) => Promise<ProjectDetail>
  updateProjectInfo: (id: string, body: { name?: string; memo?: string }) => Promise<ProjectDetail>
  moveProject: (id: string, folderId: string | null) => Promise<void>
  createFolder: (name: string, parentId: string | null) => Promise<ProjectFolder>
  renameFolder: (id: string, name: string) => Promise<void>
  moveFolder: (id: string, parentId: string | null) => Promise<void>
  deleteFolder: (id: string) => Promise<{ moved_folders: number; moved_projects: number }>
  loadProject: (id: string) => Promise<void>
  advanceStep: (step: number) => Promise<void>
  refreshGating: () => Promise<void>
  setHidden: (id: string, hidden: boolean) => Promise<void>
  removeProject: (id: string) => Promise<void>
  clearProject: () => void
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  folders: [],
  currentProject: null,
  gating: null,
  loading: false,

  fetchProjects: async () => {
    set({ loading: true })
    const { items, folders } = await projectApi.listProjects()
    set({ projects: items, folders, loading: false })
  },

  createProject: async (name, memo, folderId) => {
    const project = await projectApi.createProject({ name, memo, folder_id: folderId ?? null })
    // 2026-08-04 - 이슈 #56: 직전 프로젝트의 SMILES 잔상 제거(신규 프로젝트는 미검색 상태).
    useMoleculeStore.getState().clear()
    set((s) => ({ projects: [{ ...project }, ...s.projects], currentProject: project }))
    return project
  },

  loadProject: async (id) => {
    set({ loading: true })
    // 2026-07-18 - 이슈 #40: 이전 프로젝트 잔상 제거 후, 전 스텝 요약을 일괄 populate.
    // 접힘 카드는 children이 미마운트되어 개별 발행이 안 되므로 중앙 populate가 필수.
    useSummaryStore.getState().clearSummaries()
    useMoleculeStore.getState().clear() // 이슈 #56: SMILES 도 프로젝트 경계에서 초기화
    const project = await projectApi.getProject(id)
    set({ currentProject: project, loading: false })
    // 이슈 #77: 직전 프로젝트에서 한 작업이 «Modified» 정렬에 반영되도록 목록을 조용히 다시 읽는다.
    get().fetchProjects().catch(() => { /* 무시 */ })
    projectApi
      .getProjectSummaries(id)
      .then((m) => {
        const map: Record<number, { en: string; ko: string }> = {}
        for (const [k, v] of Object.entries(m)) map[Number(k)] = { en: (v.en || '').trim(), ko: (v.ko || '').trim() }
        useSummaryStore.getState().setSummaries(map)
      })
      .catch(() => { /* 요약 로드 실패는 워크스페이스 로드를 막지 않는다 */ })
    await get().refreshGating()
  },

  // 2026-07-09 - 진행도 확정 (이슈 #33, REQ-A2·B2): Save & Continue가 도달 스텝을
  // 서버에 기록. 서버가 clamp·단조증가 처리하므로 응답값으로 현재 프로젝트·목록 갱신.
  advanceStep: async (step) => {
    const cur = get().currentProject
    if (!cur) return
    const updated = await projectApi.updateProject(cur.id, { current_step: step })
    set((s) => ({
      currentProject: s.currentProject?.id === updated.id ? updated : s.currentProject,
      projects: s.projects.map((p) => (p.id === updated.id ? { ...p, current_step: updated.current_step } : p)),
    }))
    await get().refreshGating()
    // 이슈 #77: 스텝 저장으로 내부데이터 최종 수정일이 바뀌었다 — 목록을 다시 읽어 «Modified» 정렬에 반영.
    get().fetchProjects().catch(() => { /* 목록 갱신 실패는 진행을 막지 않는다 */ })
  },

  // 2026-07-09 - 게이팅 신호 갱신 (이슈 #33 REQ-B6). 프로젝트 로드·진행도 확정·RLD 선택 시 호출.
  refreshGating: async () => {
    const cur = get().currentProject
    if (!cur) {
      set({ gating: null })
      return
    }
    try {
      const gating = await projectApi.getGating(cur.id)
      set({ gating })
    } catch {
      set({ gating: null })
    }
  },

  // 2026-06-19 - 소프트 숨김/해제 (이슈 #7): 목록·현재 프로젝트 동기화
  setHidden: async (id, hidden) => {
    const updated = hidden ? await projectApi.hideProject(id) : await projectApi.unhideProject(id)
    set((s) => ({
      projects: s.projects.map((p) => (p.id === id ? { ...p, is_hidden: hidden } : p)),
      currentProject: s.currentProject?.id === id ? { ...s.currentProject, is_hidden: updated.is_hidden } : s.currentProject,
    }))
  },

  // 2026-07-09 - 소프트 삭제 (이슈 #35): 서버는 is_deleted 플래그만 세팅(물리 삭제 아님).
  // 목록에서 제거하고, 현재 열려있던 프로젝트를 지운 경우 워크스페이스를 초기화.
  removeProject: async (id) => {
    await projectApi.deleteProject(id)
    set((s) => ({
      projects: s.projects.filter((p) => p.id !== id),
      ...(s.currentProject?.id === id ? { currentProject: null, gating: null } : {}),
    }))
  },

  // 2026-09-21 - 이름·Description 수정 (이슈 #77, M901-02·06). 목록·헤더에 즉시 반영.
  // 내부데이터가 아니므로 data_updated_at·정렬 순서는 그대로다(BR-PRJ-03).
  updateProjectInfo: async (id, body) => {
    const updated = await projectApi.updateProject(id, body)
    set((s) => ({
      projects: s.projects.map((p) => (p.id === id ? { ...p, name: updated.name, memo: updated.memo } : p)),
      currentProject: s.currentProject?.id === id ? { ...s.currentProject, name: updated.name, memo: updated.memo } : s.currentProject,
    }))
    return updated
  },

  // 2026-09-21 - 폴더로 이동·루트로 꺼내기 (이슈 #77, BR-PRJ-06)
  moveProject: async (id, folderId) => {
    const updated = await projectApi.moveProject(id, folderId)
    set((s) => ({
      projects: s.projects.map((p) => (p.id === id ? { ...p, folder_id: updated.folder_id } : p)),
      currentProject: s.currentProject?.id === id ? { ...s.currentProject, folder_id: updated.folder_id } : s.currentProject,
    }))
  },

  // 2026-09-21 - 사용자 폴더 (이슈 #77, BR-PRJ-05~07). 규칙 위반은 서버 409 — 호출부가 문장을 보여 준다.
  createFolder: async (name, parentId) => {
    const folder = await projectApi.createFolder(name, parentId)
    set((s) => ({ folders: [...s.folders, folder] }))
    return folder
  },

  renameFolder: async (id, name) => {
    const folder = await projectApi.renameFolder(id, name)
    set((s) => ({ folders: s.folders.map((f) => (f.id === id ? folder : f)) }))
  },

  moveFolder: async (id, parentId) => {
    const folder = await projectApi.moveFolder(id, parentId)
    set((s) => ({ folders: s.folders.map((f) => (f.id === id ? folder : f)) }))
  },

  // 내용물(하위 폴더·프로젝트)은 서버가 상위로 옮긴다 — 결과를 그대로 받아오도록 목록을 다시 읽는다.
  deleteFolder: async (id) => {
    const result = await projectApi.deleteFolder(id)
    await get().fetchProjects()
    return result
  },

  clearProject: () => {
    useMoleculeStore.getState().clear()
    set({ currentProject: null, gating: null })
  },
}))
